import React from 'react';
import { useTranslation } from 'react-i18next';
import { GitBranchIcon } from '@phosphor-icons/react';
import { Button, Dialog } from '@origo/origo-ui';
import { EmptyState } from '@geodata/georg';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import RelationCard from './components/RelationCard';
import RelationEditDialog, {
    type RelationDialogMode,
    type RelationEditDialogOptions,
    type RelationFieldErrors,
} from './components/RelationEditDialog';
import type { RelationFormValues, RelationPair } from '../../types';
import styles from './RelationsPage.module.css';

interface RelationsPageProps {
    pairs: RelationPair[];
    dialogOpen: boolean;
    dialogMode: RelationDialogMode;
    dialogValues: RelationFormValues;
    dialogOptions: RelationEditDialogOptions;
    dialogFieldErrors: RelationFieldErrors;
    isSaving?: boolean;
    onCreate: () => void;
    onEdit: (id: string) => void;
    onDialogChange: (field: keyof RelationFormValues, value: string) => void;
    onDialogClose: () => void;
    onDialogSave: () => void;

    // Slett (#14732)
    onDeleteRequest: (id: string) => void;
    pendingDelete: RelationPair | null;
    onDeleteConfirm: () => void;
    onDeleteCancel: () => void;
    isDeleting?: boolean;
}

const RelationsPage: React.FC<RelationsPageProps> = ({
    pairs,
    dialogOpen,
    dialogMode,
    dialogValues,
    dialogOptions,
    dialogFieldErrors,
    isSaving,
    onCreate,
    onEdit,
    onDialogChange,
    onDialogClose,
    onDialogSave,
    onDeleteRequest,
    pendingDelete,
    onDeleteConfirm,
    onDeleteCancel,
    isDeleting,
}) => {
    const { t } = useTranslation();

    const isEmpty = pairs.length === 0;

    return (
        <article className={styles.page}>
            <PlanDetailHeader
                title={t('relations.title')}
                subtitle={t('relations.subtitle')}
                actions={
                    !isEmpty ? (
                        <Button variant="secondary" size="md" onClick={onCreate}>
                            {t('relations.newRelation')}
                        </Button>
                    ) : undefined
                }
            />
            <div className={styles.body}>
                {isEmpty ? (
                    <EmptyState
                        icon={<GitBranchIcon size={32} weight="duotone" />}
                        title={t('relations.empty.title')}
                        description={t('relations.empty.description')}
                        action={
                            <Button variant="primary" size="lg" onClick={onCreate}>
                                {t('relations.empty.action')}
                            </Button>
                        }
                    />
                ) : (
                    <>
                        <h2 className={styles.groupTitle}>{t('relations.groupTitle')}</h2>
                        <ul className={styles.list}>
                            {pairs.map(pair => (
                                <li key={pair.id}>
                                    <RelationCard pair={pair} onEdit={onEdit} onDeleteRequest={onDeleteRequest} />
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
            <RelationEditDialog
                open={dialogOpen}
                mode={dialogMode}
                values={dialogValues}
                options={dialogOptions}
                fieldErrors={dialogFieldErrors}
                onChange={onDialogChange}
                onClose={onDialogClose}
                onSave={onDialogSave}
                isSaving={isSaving}
            />
            <Dialog
                open={pendingDelete !== null}
                onClose={onDeleteCancel}
                title={t('relations.delete.title')}
                footer={
                    <div className={styles.dialogFooter}>
                        <Button variant="secondary" onClick={onDeleteCancel} disabled={isDeleting}>
                            {t('common.cancel')}
                        </Button>
                        <Button variant="danger" onClick={onDeleteConfirm} disabled={isDeleting}>
                            {t('relations.delete.confirm')}
                        </Button>
                    </div>
                }
            >
                <p>
                    {t('relations.delete.body', {
                        from: pendingDelete?.from.plannavn ?? '',
                        to: pendingDelete?.to.plannavn ?? '',
                    })}
                </p>
            </Dialog>
        </article>
    );
};

export default RelationsPage;
